import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { useOrgStore } from '@/stores/org'

export interface JoinLink {
  id: number
  project_id: number
  token: string
  is_revoked: boolean
  url: string
  [key: string]: any
}

type ProjectId = number | string

export const linkUrl = (token: string) => `${location.origin}/join/${token}`

const withUrl = (link: JoinLink): JoinLink => ({ ...link, url: linkUrl(link.token) })

const orgOf = () => useOrgStore().selectedOrg || useOrgStore().current?.organization_id

const base = (projectId: ProjectId) => `${HOST}/org/${orgOf()}/project/${projectId}/join-links`

const call = (method: string, url: string, data?: unknown) => API({ method, url, data }).then((r) => r.data)

export const useJoinLinksStore = defineStore('joinLinks', {
  state: () => ({
    byProject: {} as Record<string, JoinLink[]>,
  }),

  getters: {
    getLinks: (state) => (projectId: ProjectId): JoinLink[] => state.byProject[projectId] || [],
    getActive() {
      return (projectId: ProjectId) => this.getLinks(projectId).filter((l) => !l.is_revoked)
    },
    getRevoked() {
      return (projectId: ProjectId) => this.getLinks(projectId).filter((l) => l.is_revoked)
    },
  },

  actions: {
    setList(projectId: ProjectId, links: JoinLink[]) {
      this.byProject = { ...this.byProject, [projectId]: links }
    },

    upsertLink(link: JoinLink) {
      const list = this.byProject[link.project_id] || []
      const exists = list.some((l) => l.id === link.id)
      this.setList(link.project_id, exists ? list.map((l) => (l.id === link.id ? link : l)) : [link, ...list])
      return link
    },

    async fetchLinks(projectId: ProjectId) {
      const links = (await call('GET', base(projectId))).map(withUrl)
      this.setList(projectId, links)
      return links as JoinLink[]
    },

    async createLink({ projectId, ...fields }: { projectId: ProjectId } & Record<string, unknown>) {
      return this.upsertLink(withUrl(await call('POST', base(projectId), fields)))
    },

    async updateLink({ projectId, id, ...patch }: { projectId: ProjectId, id: number } & Record<string, unknown>) {
      return this.upsertLink(withUrl(await call('PATCH', `${base(projectId)}/${id}`, patch)))
    },

    async revokeLink({ projectId, id }: { projectId: ProjectId, id: number }) {
      return this.upsertLink(withUrl(await call('POST', `${base(projectId)}/${id}/revoke`)))
    },

    async deleteLink({ projectId, id }: { projectId: ProjectId, id: number }) {
      await call('DELETE', `${base(projectId)}/${id}`)
      this.setList(projectId, (this.byProject[projectId] || []).filter((l) => l.id !== id))
    },

    async removeJoined({ projectId, id, memberId }: { projectId: ProjectId, id: number, memberId: number }) {
      return this.upsertLink(withUrl(await call('DELETE', `${base(projectId)}/${id}/members/${memberId}`)))
    },

    async approveRequest({ projectId, id, memberId }: { projectId: ProjectId, id: number, memberId: number }) {
      return this.upsertLink(withUrl(await call('POST', `${base(projectId)}/${id}/requests/${memberId}/approve`)))
    },

    async declineRequest({ projectId, id, memberId }: { projectId: ProjectId, id: number, memberId: number }) {
      return this.upsertLink(withUrl(await call('DELETE', `${base(projectId)}/${id}/requests/${memberId}`)))
    },
  },
})
